import type { SkillPathId } from "./schemas";
import { skillNodeById } from "./skill-tree";

export const PATH_LABEL: Record<SkillPathId, string> = {
  bulwark: "Bulwark",
  glass: "Glass",
  nap: "Close Assault",
  high: "Velocity",
  scrapper: "Scrapper",
  aegis: "Aegis",
  kinetic: "Kinetic",
  arc: "Arc",
  momentum: "Momentum",
  ghost: "Ghost",
};

/** Explicit path tags so strategy is data, not guesswork. */
export const SKILL_PATH: Record<string, SkillPathId> = {
  reinforced_plating: "bulwark",
  ablative_armor: "bulwark",
  hull_weave_2: "bulwark",
  emergency_seal: "bulwark",
  composite_plating: "bulwark",
  ram_brace: "bulwark",
  keystone_bulwark: "bulwark",
  hard_shell_skill: "bulwark",
  barrier_dense: "bulwark",
  glass_hull: "glass",
  hot_rounds: "glass",
  breach_tuned_skill: "glass",
  glass_core: "glass",
  keystone_glass: "glass",
  overcharge: "glass",
  nap_trim: "nap",
  deep_nap: "nap",
  nap_hunter: "nap",
  dust_keel: "nap",
  high_trim: "high",
  high_rider: "high",
  sky_keel: "high",
  thin_air: "high",
  salvage_tuned: "scrapper",
  magnetic_sweep: "scrapper",
  loot_courier_skill: "scrapper",
  field_salvager: "scrapper",
  keystone_scavenger: "scrapper",
  quick_extract: "scrapper",
  beacon_boost: "scrapper",
  magnet_well: "scrapper",
  pad_thief: "scrapper",
};

export type BuildVerdict = {
  counts: Record<SkillPathId, number>;
  lead: SkillPathId | null;
  committed: boolean;
  conflict:
    "glass-bulwark" | "nap-high" | "kinetic-arc" | "momentum-bulwark" | "aegis-ghost" | null;
  label: string;
  detail: string;
};

export function pathOf(nodeId: string): SkillPathId | undefined {
  return skillNodeById(nodeId)?.path ?? SKILL_PATH[nodeId];
}

export function emptyPathCounts(): Record<SkillPathId, number> {
  return {
    bulwark: 0,
    glass: 0,
    nap: 0,
    high: 0,
    scrapper: 0,
    aegis: 0,
    kinetic: 0,
    arc: 0,
    momentum: 0,
    ghost: 0,
  };
}

export function countPaths(owned: readonly string[]): Record<SkillPathId, number> {
  const counts = emptyPathCounts();
  for (const id of owned) {
    const path = pathOf(id);
    if (path) counts[path] += 1;
  }
  return counts;
}

export function evaluateBuild(owned: readonly string[]): BuildVerdict {
  const counts = countPaths(owned);
  const ranked = (Object.keys(counts) as SkillPathId[]).sort((a, b) => counts[b] - counts[a]);
  const lead = ranked[0] && counts[ranked[0]] > 0 ? ranked[0] : null;
  const committed = Boolean(lead && counts[lead] >= 3);
  const conflict: BuildVerdict["conflict"] =
    counts.glass >= 2 && counts.bulwark >= 2
      ? "glass-bulwark"
      : counts.nap >= 2 && counts.high >= 2
        ? "nap-high"
        : counts.kinetic >= 2 && counts.arc >= 2
          ? "kinetic-arc"
          : counts.momentum >= 2 && counts.bulwark >= 2
            ? "momentum-bulwark"
            : counts.aegis >= 2 && counts.ghost >= 2
              ? "aegis-ghost"
              : null;

  if (conflict === "glass-bulwark") {
    return {
      counts,
      lead,
      committed: false,
      conflict,
      label: "SPLIT HULL",
      detail: "Glass + plating both half-built — hull and damage sag.",
    };
  }
  if (conflict === "nap-high") {
    return {
      counts,
      lead,
      committed: false,
      conflict,
      label: "SPLIT LANE",
      detail: "Close Assault and Velocity are both half-built — neither path pays extra.",
    };
  }
  if (conflict === "kinetic-arc") {
    return {
      counts,
      lead,
      committed: false,
      conflict,
      label: "SPLIT FIRE",
      detail: "Kinetic and arc both half-built — chambers fight each other.",
    };
  }
  if (conflict === "momentum-bulwark") {
    return {
      counts,
      lead,
      committed: false,
      conflict,
      label: "SPLIT MASS",
      detail: "Heavy plate plus afterburn — neither hull nor speed pays.",
    };
  }
  if (conflict === "aegis-ghost") {
    return {
      counts,
      lead,
      committed: false,
      conflict,
      label: "SPLIT SIGNATURE",
      detail: "Ward and fade both half-built — you are loud and thin.",
    };
  }
  if (committed && lead) {
    return {
      counts,
      lead,
      committed: true,
      conflict: null,
      label: `${PATH_LABEL[lead].toUpperCase()} LOCK`,
      detail: `Committed ${PATH_LABEL[lead]} — path bonus is live.`,
    };
  }
  if (lead && counts[lead] >= 2) {
    return {
      counts,
      lead,
      committed: false,
      conflict: null,
      label: `LEAN ${PATH_LABEL[lead].toUpperCase()}`,
      detail: `One more ${PATH_LABEL[lead]} node locks the bonus.`,
    };
  }
  return {
    counts,
    lead,
    committed: false,
    conflict: null,
    label: "OPEN BUILD",
    detail:
      "Pick a path: Bulwark, Glass, Close Assault, Velocity, Scrapper, Aegis, Kinetic, Arc, Momentum, or Ghost.",
  };
}

export type OfferVerdict = "synergy" | "conflict" | "neutral";

export function verdictForOffer(owned: readonly string[], nextId: string): OfferVerdict {
  const nextPath = pathOf(nextId);
  if (!nextPath) return "neutral";
  const nextOwned = [...owned, nextId];
  const after = evaluateBuild(nextOwned);
  if (after.conflict) return "conflict";
  const before = evaluateBuild(owned);
  if (after.committed && !before.committed) return "synergy";
  if (after.lead === nextPath && (after.counts[nextPath] ?? 0) >= 2) return "synergy";
  return "neutral";
}

export function applyPathModifiers(
  owned: readonly string[],
  mods: {
    damageMult: number;
    hullBonus: number;
    speedMult: number;
    salvageMult: number;
    armorBonus: number;
  },
): void {
  const verdict = evaluateBuild(owned);
  if (verdict.conflict === "glass-bulwark") {
    mods.damageMult *= 0.92;
    mods.hullBonus -= 12;
    return;
  }
  if (verdict.conflict === "nap-high") {
    mods.speedMult *= 0.94;
    mods.damageMult *= 0.96;
    return;
  }
  if (verdict.conflict === "kinetic-arc") {
    mods.damageMult *= 0.88;
    return;
  }
  if (verdict.conflict === "momentum-bulwark") {
    mods.speedMult *= 0.92;
    mods.hullBonus -= 10;
    return;
  }
  if (verdict.conflict === "aegis-ghost") {
    mods.armorBonus -= 8;
    mods.speedMult *= 0.95;
    return;
  }
  if (!verdict.committed || !verdict.lead) return;
  switch (verdict.lead) {
    case "bulwark":
      mods.hullBonus += 18;
      mods.armorBonus += 8;
      break;
    case "glass":
      mods.damageMult *= 1.1;
      break;
    case "nap":
    case "high":
      mods.speedMult *= 1.06;
      mods.damageMult *= 1.05;
      break;
    case "scrapper":
      mods.salvageMult *= 1.12;
      break;
    case "aegis":
      mods.armorBonus += 12;
      mods.hullBonus += 8;
      break;
    case "kinetic":
      mods.damageMult *= 1.12;
      break;
    case "arc":
      mods.damageMult *= 1.08;
      mods.speedMult *= 1.03;
      break;
    case "momentum":
      mods.speedMult *= 1.12;
      break;
    case "ghost":
      mods.speedMult *= 1.06;
      mods.salvageMult *= 1.06;
      break;
  }
}
